import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { LesseeService } from './lessee.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authResponse = null;

  constructor(
    private client: HttpClient,
    private router: Router,
    private lesseeService: LesseeService
  ) { 
    try {
      const raw = localStorage.getItem('auth');
      this.authResponse = raw ? JSON.parse(raw) : null;
    } catch {
      this.authResponse = null;
    }
  }

  isAuthenticated() {
    return !!this.authResponse;
  }

  authenticate(authType: string, username: string, password: string) {
      return this.client.post(`${environment.apiBaseUrl}/v1/auth/login`, {
        "authType": authType,
        "username": username,
        "password": password
      }).toPromise().then((res: any) => {
        this.authResponse = res;
        localStorage.setItem("auth", JSON.stringify(this.authResponse));
        this.lesseeService.clearCache();
        return this.authResponse;
      });
  }

  authenticateWithToken(authType: string, token: string) {
      return this.client.post(`${environment.apiBaseUrl}/v1/auth/login`, {
        "authType": authType,
        "token": token
      }).toPromise().then((res: any) => {
        this.authResponse = res;
        localStorage.setItem("auth", JSON.stringify(this.authResponse));
        this.lesseeService.clearCache();
        return this.authResponse;
      });
  }

  refreshToken() {
    return this.client.post(`${environment.apiBaseUrl}/v1/auth/refreshToken`, {
    }).toPromise().then((res: any) => {
      this.authResponse = res;
      localStorage.setItem("auth", JSON.stringify(this.authResponse));
      return this.authResponse;
    });
  }

  getToken() {
    // Token can be at authResponse.token (new format) or authResponse.token (legacy)
    if (this.authResponse && this.authResponse.token) {
      return this.authResponse.token;
    }
    if (this.authResponse && this.authResponse.user && this.authResponse.user.token) {
      return this.authResponse.user.token;
    }
    return null;
  }

  getAuthentication() {
    // Return user object if it exists, otherwise return the full response (for legacy compatibility)
    if (this.authResponse && this.authResponse.user) {
      return this.authResponse.user;
    }
    return this.authResponse;
  }

  private ensureRolesArray(roles: any): string[] {
    if (Array.isArray(roles)) return roles;
    if (roles == null || roles === '') return [];
    if (typeof roles === 'string') {
      try {
        const parsed = JSON.parse(roles);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [roles];
      }
    }
    return [];
  }

  isAdmin(): boolean {
    if (!this.isAuthenticated() || !this.authResponse) return false;
    const u = this.authResponse.user || this.authResponse;
    const fromUser = u ? (u.roles != null ? u.roles : u.role) : null;
    const raw = fromUser != null ? fromUser : (this.authResponse.roles != null ? this.authResponse.roles : this.authResponse.role);
    const roles = this.ensureRolesArray(raw);
    for (let i = 0; i < roles.length; i++) {
      const r = String(roles[i]).toUpperCase();
      if (r === 'SITEADMIN' || r === 'SITE_ADMIN' || r === 'SUPERADMIN' || r === 'SUPER_ADMIN') return true;
    }
    return false;
  }

  isLessee(): boolean {
    if (!this.isAuthenticated() || !this.authResponse) return false;
    const u = this.authResponse.user || this.authResponse;
    const fromUser = u ? (u.roles != null ? u.roles : u.role) : null;
    const raw = fromUser != null ? fromUser : (this.authResponse.roles != null ? this.authResponse.roles : this.authResponse.role);
    const roles = this.ensureRolesArray(raw);
    for (let i = 0; i < roles.length; i++) {
      const r = String(roles[i]).toUpperCase();
      if (r === 'LEASE' || r === 'LEASEE') return true;
    }
    return false;
  }

  isSuperAdmin(): boolean {
    if (!this.isAuthenticated() || !this.authResponse) return false;
    const u = this.authResponse.user || this.authResponse;
    const fromUser = u ? (u.roles != null ? u.roles : u.role) : null;
    const raw = fromUser != null ? fromUser : (this.authResponse.roles != null ? this.authResponse.roles : this.authResponse.role);
    const roles = this.ensureRolesArray(raw);
    for (let i = 0; i < roles.length; i++) {
      const r = String(roles[i]).toUpperCase();
      if (r === 'SUPERADMIN' || r === 'SUPER_ADMIN') return true;
    }
    return false;
  }

  logout() {
    this.authResponse = null;
    localStorage.removeItem("auth");
    this.lesseeService.clearCache();
    this.router.navigate(['']);
  }

  resetPassword(token, userId, password) {
    return this.client.post(`${environment.apiBaseUrl}/v1/auth/resetPassword`, {
      "token": token,
      "userId": userId,
      "password": password
    }).toPromise().then((res: any) => {
      this.authResponse = res;
      localStorage.setItem("auth", JSON.stringify(this.authResponse));
      return this.authResponse;
    });
  }
}
